import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext(null);

function socketBaseUrl() {
  const fromEnv = process.env.REACT_APP_SOCKET_URL;
  if (fromEnv) return fromEnv.replace(/\/$/, '');
  const api = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  try {
    return new URL(api).origin;
  } catch {
    return 'http://localhost:5000';
  }
}

export function SocketProvider({ children }) {
  const { token, isAuthenticated } = useAuth();
  const [connected, setConnected] = useState(false);
  const [toast, setToast] = useState(null);
  const messageListenersRef = useRef(new Set());

  const subscribeMessages = useCallback((fn) => {
    messageListenersRef.current.add(fn);
    return () => messageListenersRef.current.delete(fn);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !token) return;
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      const t = setTimeout(() => {
        Notification.requestPermission().catch(() => {});
      }, 5000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      setConnected(false);
      return undefined;
    }

    const socket = io(socketBaseUrl(), {
      auth: { token },
      transports: ['websocket', 'polling']
    });

    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));

    socket.on('message:new', (payload) => {
      messageListenersRef.current.forEach((fn) => {
        try {
          fn(payload);
        } catch (e) {
          console.error(e);
        }
      });
    });

    socket.on('notification', (n) => {
      const id = Date.now();
      setToast({
        id,
        title: n.title || 'Notification',
        body: n.body || '',
        conversationId: n.conversationId
      });
      if (
        typeof Notification !== 'undefined' &&
        Notification.permission === 'granted' &&
        document.visibilityState === 'hidden'
      ) {
        try {
          new Notification(n.title || 'Labster', {
            body: n.body || '',
            tag: n.conversationId ? `conv-${n.conversationId}` : `n-${id}`
          });
        } catch (_) {
          /* ignore */
        }
      }
      setTimeout(() => {
        setToast((t) => (t && t.id === id ? null : t));
      }, 6500);
    });

    return () => {
      socket.disconnect();
      setConnected(false);
    };
  }, [isAuthenticated, token]);

  return (
    <SocketContext.Provider value={{ connected, subscribeMessages }}>
      {children}
      {toast && (
        <div
          className="socket-toast"
          role="status"
          onClick={() => setToast(null)}
          onKeyDown={(e) => e.key === 'Enter' && setToast(null)}
          tabIndex={0}
        >
          <strong>{toast.title}</strong>
          {toast.body && <p>{toast.body}</p>}
        </div>
      )}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  const ctx = useContext(SocketContext);
  if (!ctx) {
    return { connected: false, subscribeMessages: () => () => {} };
  }
  return ctx;
}
